<script setup lang="ts">
import { ref, computed } from 'vue'

import { ImageSrcSetFormat } from '@xcel-next/rendering-tools/types/constants'

import { useImgSrcSet } from '../../composables/use-img-src-set'
import { useCdnImages } from '../../composables/use-cdn-images'
import Date from './Date.vue'

import type { ComposedParagraphTeaser } from '@xcel-next/composer-schema'
import type { Brand } from '@xcel-next/core/enums'

type AuthorSettings = {
  brand?: Brand
  displayDate?: string | false
  isPreview?: boolean
  isContentBoxElement?: boolean
}

const props = withDefaults(defineProps<{
  composedAuthors?: ComposedParagraphTeaser[]
  settings?: AuthorSettings
  imageSrcSetName?: string
}>(), {
  composedAuthors: () => [],
  settings: () => ({
    brand: undefined,
    displayDate: undefined,
    isPreview: false,
    isContentBoxElement: false
  }),
  imageSrcSetName: 'author'
})

const { createImgSrc, getImageAltText } = useImgSrcSet()
const { cdnImageUrl } = useCdnImages()

// only used in gui preview
const previewAuthors = ref<ComposedParagraphTeaser[]>([])

const styleClasses = computed(() => ({
  'bx-content': true,
  'bx-content__author': true,
  'bx-content__author-none': authorList.value.length <= 0,
  'bx-typo--content-author': true
}))

const authorList = computed(() => !props.settings?.isPreview
  ? props.composedAuthors
  : previewAuthors.value)

const authorJobTitle = (author: ComposedParagraphTeaser) =>
  author.jobTitle?.length ?? 0 > 0 ? `${!props.settings?.isContentBoxElement ? ', ' : ''}${author.jobTitle}` : ''

const authorInfo = (author: ComposedParagraphTeaser) =>
  (author.titleLong || author.titleShort || '') +
  (!props.settings?.isContentBoxElement ? authorJobTitle(author) : '')

const imageUrl = (author: ComposedParagraphTeaser) =>
  createImgSrc({
    image: author,
    brand: props.settings?.brand ?? '',
    setName: props.imageSrcSetName,
    type: 'webp',
    srcSetDefinition: {
      format: ImageSrcSetFormat.Square
    }
  })
</script>

<template>
  <div :class="styleClasses">
    <div class="bx-content__author-wrapper">
      <a v-for="(author, id) in authorList"
         :key="id"
         :target="author.target ? author.target : '_self'"
         :href="author.url"
         class="bx-typo--text-decoration-none">
        <figure v-if="imageUrl(author)"
                class="bx-content__author-image">
          <img :src="imageUrl(author) ?? undefined"
               width="32"
               height="32"
               :alt="getImageAltText(author, authorInfo(author))"
               :title="getImageAltText(author, authorInfo(author))">
        </figure>
        <figure v-if="settings?.isPreview && !imageUrl(author)"
                class="bx-content__author-image">
          <img :src="cdnImageUrl('/images/default-placeholder.jpg')"
               width="32"
               height="32">
        </figure>
        <span class="bx-content__author-name">
          {{ authorInfo(author) }}
          <span v-if="settings?.isContentBoxElement"
                class="bx-content__author-job-title">
            {{ authorJobTitle(author) }}
          </span>
        </span>
      </a>
    </div>
    <Date v-if="settings?.displayDate"
          :date-time="settings.displayDate" />
  </div>
</template>
